import React from 'react';
import { Chart } from 'react-google-charts';
import { Card, CardBody, CardHeader } from '../../components/Bootstrap';
import { differenceInCalendarDays, startOfDay, addDays, format } from 'date-fns';
import { ButtonSelect } from '../../components/ButtonSelect';
import { DashboardQuery_dashboardStats_revenue as RevenueData } from './__generated__/DashboardQuery';

const intervals = ['Min', 'Hour', 'Day', 'Week', 'Month'];
const formatForInterval = (interval) => {
  switch (interval) {
    case 'minute':
    case 'hour':
      return 'MMMM d, h:mm b';
    default:
      return 'MMMM d';
  }
};

interface RevenueGraphProps {
  interval: string;
  revenue: RevenueData[];
  changeInterval(interval: string);
}

export class RevenueGraph extends React.Component<RevenueGraphProps> {
  static defaultProps = {
    interval: 'day',
  };

  constructor(props) {
    super(props);

    this.renderGraph = this.renderGraph.bind(this);
  }

  tooltipHTML(row: RevenueData) {
    const dateFormat = formatForInterval(this.props.interval);
    return `<p class="mb-0">${format(new Date(row.date.replace('Z', '')), dateFormat)} - $${row.amount}</p>`;
  }

  graphOptionsForRevenue(revenue: RevenueData[]) {
    let ticks;

    if (this.props.interval === 'day' && revenue.length > 1) {
      const first = startOfDay(new Date(revenue[0].date));
      const last = startOfDay(new Date(revenue[revenue.length - 1].date));
      const diff = differenceInCalendarDays(last, first);

      ticks = [];

      for (let i = 0; i < diff; i++) {
        const d = addDays(first, i);
        ticks.push(d);
      }
    }

    return {
      options: {
        titlePosition: 'none',
        axisTitlesPosition: 'none',
        interpolateNulls: true,
        chartArea: {
          width: '95%',
          height: '85%',
        },
        colors: ['#31358f'],
        lineWidth: 1,
        hAxis: {
          ticks,
          gridlines: {
            color: 'none',
            count: -1,
            units: {
              days: {
                format: ['MMM dd'],
              },
              hours: {
                format: ['hh:mm a', 'ha'],
              },
            },
          },
          format: 'MMM dd',
          // gridlines: {  },
          textStyle: {
            color: '#1A1A31',
            fontName: 'Avenir',
          },
          titleTextStyle: {
            color: '#1A1A31',
            fontName: 'Avenir',
            fontSize: 10,
          },
        },
        vAxis: {
          baselineColor: '#817F90',
          format: 'currency',
          textPosition: 'in',
          gridlines: {
            color: '#817F90',
          },
          textStyle: {
            fontName: 'Avenir',
            fontSize: 12,
            color: '#817F90',
          },
        },
        legend: 'none',
        tooltip: { isHtml: true },
        pointSize: 10,
        backgroundColor: 'transparent',
      },
      rows: revenue.map((row) => [
        new Date(row.date.replace('Z', '')),
        row.amount,
        this.tooltipHTML(row),
        'fill-color: #31318c; stroke-color: #fff;',
      ]),
      columns: [
        {
          type: 'date',
          label: 'Date',
        },
        {
          type: 'number',
          label: 'Revenue',
        },
        {
          role: 'tooltip',
          type: 'string',
          p: { html: true },
        },
        {
          role: 'style',
          type: 'string',
        },
      ],
    };
  }

  renderLoadingMessage() {
    return <div className='text-center'>Loading...</div>;
  }

  renderGraph() {
    const options = this.graphOptionsForRevenue(this.props.revenue);
    const props: any = {
      className: 'revenue-chart',
      chartType: 'AreaChart',
      loader: this.renderLoadingMessage(),
      columns: options.columns,
      rows: options.rows,
      options: options.options,
      graph_id: 'ScatterChart',
      width: '100%',
      height: '400px',
      legend_toggle: true,
    };

    return (
      <CardBody className='revenue-graph-body'>
        <div className='revenue-interval-selection'>
          <ButtonSelect
            type='button'
            options={intervals}
            onChange={this.props.changeInterval}
            selected={this.props.interval}
          />
        </div>
        <Chart {...props} />
      </CardBody>
    );
  }

  render() {
    return (
      <Card className='card-bottom-margin revenue-graph border-0'>
        <CardHeader className='revenue-title'>Total Revenue</CardHeader>
        {this.renderGraph()}
      </Card>
    );
  }
}
